/**
 * One-time informational modal shown before the first workout on
 * Android. Explains that precise location is used ONLY during an
 * active workout via Android's foreground location service.
 *
 * NOT a Google Play Prominent Disclosure: Slimmy no longer requests
 * ACCESS_BACKGROUND_LOCATION. Tracking uses only ACCESS_FINE_LOCATION /
 * ACCESS_COARSE_LOCATION plus a foreground service of type "location".
 */
import { MapPin } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const KEY_SEEN = 'slimmy_location_info_seen';

// Legacy purge — flags from the removed ACCESS_BACKGROUND_LOCATION
// flow. One-shot on module load.
try {
  [
    'slimmy_disclosure_v2_accepted',
    'slimmy_bg_location_granted_v2',
    'slimmy_disclosure_v2_just_shown_at',
    'slimmy_bg_location_disclosure',
  ].forEach((k) => localStorage.removeItem(k));
} catch { /* noop */ }

export function isAndroid() {
  try {
    // eslint-disable-next-line global-require
    const { Capacitor } = require('@capacitor/core');
    return Capacitor.getPlatform && Capacitor.getPlatform() === 'android';
  } catch { return false; }
}

export function hasSeenLocationInfo() {
  try { return localStorage.getItem(KEY_SEEN) === 'true'; } catch { return true; }
}

export function markLocationInfoSeen() {
  try { localStorage.setItem(KEY_SEEN, 'true'); } catch { /* noop */ }
}

export default function LocationInfoModal({ open, isDark, onAccept, onDecline }) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
      data-testid="location-info-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-info-title"
    >
      <div
        className={`w-full max-w-md rounded-3xl overflow-hidden ${
          isDark ? 'bg-[#111827] border border-white/10' : 'bg-white shadow-2xl'
        }`}
        data-testid="location-info-modal"
      >
        <div className="px-6 pt-6 pb-4 flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(232, 51, 74, 0.12)' }}
          >
            <MapPin className="w-6 h-6 text-[#E8334A]" />
          </div>
          <h2
            id="location-info-title"
            className={`text-lg font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            {t('locationInfo.title')}
          </h2>
        </div>

        <div className={`px-6 pb-5 space-y-3 text-sm leading-relaxed ${
          isDark ? 'text-white/80' : 'text-gray-700'
        }`}>
          <p>{t('locationInfo.body1')}</p>
          <p>{t('locationInfo.body2')}</p>
          <p>{t('locationInfo.body3')}</p>
        </div>

        <div className="px-6 pb-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={onAccept}
            className="w-full py-3.5 rounded-2xl font-semibold text-white shadow-md shadow-red-500/20 transition-transform bg-[#E8334A] active:scale-[0.98]"
            data-testid="location-info-accept"
          >
            {t('locationInfo.accept')}
          </button>
          <button
            type="button"
            onClick={onDecline}
            className={`w-full py-3.5 rounded-2xl font-medium ${
              isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'
            }`}
            data-testid="location-info-decline"
          >
            {t('locationInfo.decline')}
          </button>
        </div>
      </div>
    </div>
  );
}
